import React from 'react';
import { Sparkles } from 'lucide-react';
import './login.css';

/**
 * Shared cinematic 3D showcase used as the left pane on /login and /signup.
 * Auto-rotating CSS coverflow ring of the 5 user-uploaded AV reference photos,
 * backed by glow orbs, scanning beams, floor reflection and a brand badge.
 *
 * Props:
 *   - headline        : React node shown as the main H2
 *   - sublineHtml     : Optional sub-text under the headline
 */
const SHOWCASE_IMAGES = [
  {
    src: 'https://customer-assets.emergentagent.com/job_af71971a-7070-41ea-a84e-7f3fc04f3ffb/artifacts/uddlsmm6_Proejction%20Mapping%20001.png',
    eyebrow: 'Architectural Mapping',
    title: 'Multi-projector facade blends'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_af71971a-7070-41ea-a84e-7f3fc04f3ffb/artifacts/3ekrx4ca_LED%20Screen.png',
    eyebrow: 'LED Engine',
    title: '10,000+ tiles, every wall mode'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_af71971a-7070-41ea-a84e-7f3fc04f3ffb/artifacts/u9ktc5z0_Truss%20Holography.png',
    eyebrow: 'Truss · Holography',
    title: 'Ground-supported goalpost truss'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_af71971a-7070-41ea-a84e-7f3fc04f3ffb/artifacts/auwuk6ae_Scafolding.png',
    eyebrow: 'Scaffolding · Rigging',
    title: 'Multi-level stacked fixture towers'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_af71971a-7070-41ea-a84e-7f3fc04f3ffb/artifacts/6cgwap1m_Proejction%20mapping.png',
    eyebrow: 'Projection Mapping',
    title: 'Pre-vis every beam, blend & mask'
  }
];

const Auth3DShowcase = ({
  headline = (<>Where every beam <br /><span className="auth-glass-title">is designed in light.</span></>),
  subline = 'Pre-visualise your entire show — scaffold, truss, LED and projection — before load-in.'
}) => {
  const count = SHOWCASE_IMAGES.length;
  const radius = 360;
  return (
    <div className="auth-left-pane relative h-full w-full overflow-hidden auth-grain" data-testid="auth-3d-showcase">
      {/* Deep background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,#0a1226_0%,#04060f_55%,#000_100%)]" />

      {/* Ambient glow orbs */}
      <div className="auth-orb o1" />
      <div className="auth-orb o2" />
      <div className="auth-orb o3" />

      {/* Scanning laser beams */}
      <div className="auth-beam b1" />
      <div className="auth-beam b2" />
      <div className="auth-beam b3" />
      <div className="auth-beam b4" />

      {/* 3D stage */}
      <div className="auth-stage">
        <div className="auth-floor" />
        <div className="auth-ring">
          {SHOWCASE_IMAGES.map((img, i) => {
            const angle = (360 / count) * i;
            return (
              <div
                key={i}
                className="auth-card"
                style={{ transform: `rotateY(${angle}deg) translateZ(${radius}px)` }}
                data-testid={`auth-showcase-card-${i}`}
              >
                <img src={img.src} alt={img.title} loading={i < 2 ? 'eager' : 'lazy'} />
                <div className="auth-card-label">
                  <div className="eyebrow">{img.eyebrow}</div>
                  <div className="title">{img.title}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Brand badge (top-left) */}
      <div className="absolute top-8 left-8 z-10 auth-fade-up" style={{ animationDelay: '0.1s' }}>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/10 backdrop-blur">
          <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
          <span className="text-[10px] tracking-[0.28em] uppercase text-cyan-100">
            Projection · LED · Truss · Simulation
          </span>
        </div>
        <h2 className="mt-4 text-3xl font-bold text-white tracking-tight leading-tight max-w-sm">
          {headline}
        </h2>
        <p className="mt-2 text-zinc-400 text-sm max-w-xs leading-relaxed">
          {subline}
        </p>
      </div>
    </div>
  );
};

export default Auth3DShowcase;
